<template>
  <StepperTemplate :returnRoute="{ to: homeRoute }" :narrowBody="true" :showSteps="false">
    <template v-slot:title>
      <p v-if="titleText" class="h2-stepper-title text-center font-weight-bold text-blue-500 mb-3">
        {{ $t(`${titleText}`) }}
      </p>
    </template>
    <template v-slot:body class="mb-5">
      <div class="stepper-body-card">
        <div v-if="isLastRequest !== true" class="text-center py-5 bg-mint">
          <SummarySpinner />
        </div>
        <div v-else>
          <p class="text-center my-4" :class="isSuccess ? 'text-positive-200' : 'text-warning-300'">
            <b-icon-check-circle-fill
              v-if="isSuccess && isPaid"
              style="width: 64px; height: 64px"
            />
            <span v-else-if="isSuccess" class="pending-payment mx-auto"></span>
            <b-icon-x-circle-fill v-else style="width: 64px; height: 64px" />
          </p>
          <div
            v-if="isSuccess && isPaid"
            v-orphans
            class="text-lg text-center text-blue-500 font-weight-bold"
          >
            <p class="mb-1">{{ $t(`form.summary.success.realized.${successText}`) }}</p>
            <p class="mb-4">{{ $t('form.summary.success.thankYou') }}</p>
          </div>
          <div v-else-if="isSuccess" class="text-left mb-4 px-lg-5">
            <p class="font-weight-bold text-lg text-center">
              {{ $t('form.summary.waiting.dearDonor') }}
            </p>
            <div class="px-md-5 mx-auto" v-html="waitingContent"></div>
          </div>
          <div v-else class="text-lg text-blue-500 mx-3" v-orphans>
            <p class="mb-1">{{ $t('form.summary.failure.dearDonor') }}</p>
            <p class="text-base font-weight-bold mb-4">
              {{ $t('form.summary.failure.notRealized') }}
            </p>
          </div>

          <div v-if="isSuccess && isPaid" class="text-blue-500 text-center my-5" v-orphans>
            <p class="mb-0">
              {{ $t(`form.summary.success.share.${translationId}.1`) }}
            </p>
            <p class="mb-4">{{ $t(`form.summary.success.share.${translationId}.2`) }}</p>
            <div class="summary-social-box">
              <ShareNetwork network="facebook" title="" :url="urlToCopy">
                <div class="summary-social">
                  <span class="summary-social-facebook-icon" />
                </div>
              </ShareNetwork>
              <a :href="Links.socialLinks.instagram" class="summary-social">
                <span class="summary-social-instagram-icon" />
                <span class="d-none">instagram</span>
              </a>
              <input type="hidden" id="textToCopy" :value="urlToCopy" />
              <a @click.prevent="copyUrl" :href="urlToCopy" class="summary-social-copy">
                <span class="summary-social-copy-icon mr-2" />
                {{ t('form.summary.success.copyLink', translationId) }}</a
              >
            </div>
          </div>
          <div v-else-if="isSuccess !== true" class="text-blue-500 mx-3" v-orphans>
            <p class="mb-3" v-html="$t('form.summary.failure.possibleReasons')"></p>
            <p class="mb-3">{{ $t('form.summary.failure.emailInfo') }}</p>
            <p class="mb-3 font-italic">{{ $t('form.summary.failure.spamInfo') }}</p>
            <p class="mb-3">
              {{ $t('form.summary.failure.contactUsStart') }}
              <a :href="'mailto:' + $t('form.typeOfSupport.helpDesk.description.mail')">
                {{ $t('form.typeOfSupport.helpDesk.description.mail') }}</a
              >
              {{ $t('form.summary.failure.contactUsEnding') }}
            </p>
            <p class="mb-3">{{ $t('form.summary.failure.paymentInfo') }}</p>
            <BankTransferData :amount="convertToMajorUnit(amount)" :order="orderId" />
            <p class="my-3">{{ $t('form.summary.failure.thankText') }}</p>
            <p class="mb-3">{{ $t('form.summary.failure.sorryText') }}</p>
          </div>
        </div>
      </div>
    </template>
  </StepperTemplate>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue'
import StepperTemplate from '@/components/widgets/StepperTemplate.vue'
import BankTransferData from '@/components/widgets/bankTransferData.vue'
import SummarySpinner from '@/components/widgets/summary/SummarySpinner.vue'
import { SOFT_PAYMENT_LIMIT, PAYMENT_STATUS } from '@/constants/payment'
import Links from '@/constants/links'
import useDataLayerStore from '@/stores/dataLayer'
// @ts-ignore
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'

import type { SummaryData, Maybe } from '@/types'
import { PaymentType } from '@/types'
import { useI18n } from 'vue-i18n'
import { ref } from 'vue'
import { useSummaryDataStore } from '@/stores/summary'
import { convertToMajorUnit } from '@/helpers/money'

const vuexStore = useStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const summaryDataStore = useSummaryDataStore()
const storageData = ref<Maybe<SummaryData>>()
storageData.value = summaryDataStore.getSummaryData

const dataLayerStore = useDataLayerStore()
dataLayerStore.sendInfoToGtm()

summaryDataStore.reset()

const sessionId = computed<Maybe<string>>(() => storageData.value?.sessionId)
if (sessionId.value) {
  vuexStore.dispatch('payment/getPaymentStatus', storageData.value?.sessionId)
} else {
  router.push({ name: 'Home' })
}

const homeRoute = reactive({
  name: 'Home',
  text: 'form.summary.backButton'
})

const getStatusResponse = computed(() => vuexStore.getters['payment/getStatusResponse'])

const isLastRequest = computed<boolean>(() => vuexStore.getters['payment/isLastRequest'])

const orderId = computed<string>(() => {
  const payuOrderId =
    (Array.isArray(route.query.orderId) ? route.query.orderId[0] : route.query.orderId) ?? ''
  const pattern = /_(.*?)_/g
  const payuOrderIdArray = payuOrderId.match(pattern) || []
  return payuOrderIdArray[0]?.slice(1, -1) || ''
})

const transactionType = computed<Maybe<number>>(() => storageData.value?.type)

const childEditionRetentionHash = computed<string | null>(
  () => storageData.value?.childEditionRetentionHash ?? null
)

const successText = computed<number>(() => {
  if (transactionType.value === PaymentType.instalments) return PaymentType.instalments
  if (
    transactionType.value === PaymentType.fundraising ||
    transactionType.value === PaymentType.fundraisingDonate
  )
    return PaymentType.fundraising
  return PaymentType.purchase
})

const amount = computed<number>(() => storageData.value?.amount ?? 0)

const fundraisingPayment = computed<number>(() => storageData.value?.fundraisingPayment ?? 0)

const totalAmount = computed<number>(
  () => parseInt(amount.value.toString(), 10) + parseInt(fundraisingPayment.value.toString(), 10)
)

const isInstalment = computed<boolean>(() => transactionType.value === PaymentType.instalments)

const translationId = computed<string>(() => {
  const FUNDRAISING_CLOSED = '1'
  const FUNDRAISING_OPEN = '2'

  const paymentLimit = SOFT_PAYMENT_LIMIT * 100
  const isMaxAmount = amount.value >= paymentLimit || totalAmount.value >= paymentLimit

  if (!storageData.value || isMaxAmount || isInstalment.value) {
    return FUNDRAISING_CLOSED
  }
  return FUNDRAISING_OPEN
})

const isSuccess = computed<boolean>(() =>
  [PAYMENT_STATUS.waiting, PAYMENT_STATUS.paid].includes(getStatusResponse.value?.status?.id)
)
const isPaid = computed<boolean>(() => getStatusResponse.value?.status?.id === PAYMENT_STATUS.paid)

const titleText = computed<string>(() => {
  if (!getStatusResponse.value) {
    return ''
  }
  if (isSuccess.value && isPaid.value) {
    return 'form.summary.success.title'
  }
  return isSuccess.value ? 'form.summary.waiting.title' : 'form.summary.failure.title'
})

const urlToChildCard = computed<string>(() =>
  childEditionRetentionHash.value
    ? router.resolve({
        name: 'ChildPageRetention',
        params: {
          childEditionId: storageData.value?.childEditionId,
          childEditionRetentionHash: childEditionRetentionHash.value
        }
      }).href
    : router.resolve({
        name: 'ChildPage',
        params: {
          childEditionId: storageData.value?.childEditionId
        }
      }).href
)

const urlToHome = computed<string>(() => router.resolve({ name: 'Home' }).href)

const urlBase = computed<string>(() => {
  const { origin } = window.location
  return `${origin}`
})

const urlToCopy = computed<string>(
  () => urlBase.value + (translationId.value === '1' ? urlToHome.value : urlToChildCard.value)
)

function copyUrl(): void {
  const urlToCopyElement: HTMLInputElement | null = document.querySelector('#textToCopy')
  if (urlToCopyElement) {
    urlToCopyElement.setAttribute('type', 'text')
    urlToCopyElement.select()
  }

  try {
    document.execCommand('copy')
  } catch (err) {
    console.warn(err)
  }

  if (urlToCopyElement) urlToCopyElement.setAttribute('type', 'hidden')
  const windowSelection = window.getSelection()
  if (windowSelection) windowSelection.removeAllRanges()
}

const isNextInstalment = computed<boolean>(() => route.name === 'SummaryNextInstallment')

const waitingContent = computed<string>(() => {
  return isNextInstalment.value
    ? t('form.summary.waiting.contentNextInstalment')
    : t('form.summary.waiting.contentFirstInstalment')
})
</script>
